import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./TabooGame.css";
import { useScribbleGame } from "../../hooks/useScribbleGame";
import { postChatGuess, enterRoundEnd } from "../../lib/scribbleGame";
import { releaseTabooWord, flipTabooCard, pickAlternateTabooCard, startTabooRound } from "../../lib/tabooGame";
import { useLobbyWords } from "../../hooks/useLobbyWords";
import Swal from "sweetalert2";
import { fetchLeaderboard } from "../../functions/fetchLeaderboard";
import {
  pointsForCorrectGuess,
  pointsForDrawerOnCorrect,
  recordCorrectGuess,
  recordDrawerBonus,
  finalizeScribblePlayer,
  markScribbleGameStarted,
} from "../../lib/scribbleScoring";
import { syncScribbleScoreToExtraMile } from "../../lib/scribbleExtraMile";
import { normalizeGuess } from "../../lib/guessMatch";
import { wordOptionsForTurn } from "../../lib/scribbleGame";
import "../SelectNextPlayer/SelectNextPlayer.css";

function guessStatusClass(status, label) {
  if (status === "correct" || String(label || "").startsWith("+")) return "tg-guess--correct";
  if (status === "taboo") return "tg-guess--taboo";
  if (status === "wrong") return "tg-guess--wrong";
  if (status === "close") return "tg-guess--close";
  return "tg-guess--neutral";
}

/** Per-viewer chat label — close/taboo/wrong hints stay private where needed. */
function chatRowDisplay(msg, viewerUserId) {
  const authorId = String(msg?.userId ?? "");
  const isMine = viewerUserId ? authorId === String(viewerUserId) : false;
  const playerName = String(msg?.name || "Player");

  if (msg.status === "correct") {
    if (isMine) {
      return {
        label: msg.label || "Congratulations!",
        cls: "tg-guess--correct",
      };
    }
    return { label: `${playerName} guessed the word!`, cls: "tg-guess--correct" };
  }

  if (msg.status === "taboo") {
    if (isMine) {
      return { label: msg.label || "You guessed a taboo word", cls: "tg-guess--taboo" };
    }
    return { label: `${playerName} guessed the taboo word`, cls: "tg-guess--taboo" };
  }

  if (msg.status === "close") {
    if (isMine) {
      return { label: "You are close", cls: "tg-guess--close" };
    }
    return { label: "", cls: "tg-guess--neutral" };
  }

  if (msg.status === "wrong") {
    if (isMine) {
      return { label: "Wrong", cls: "tg-guess--wrong" };
    }
    return { label: "", cls: "tg-guess--neutral" };
  }

  return { label: msg.label || "", cls: guessStatusClass(msg.status, msg.label) };
}

const CONFETTI_COLORS = [
  "#ffd166",
  "#ef476f",
  "#06d6a0",
  "#118ab2",
  "#8338ec",
  "#f77f00",
  "#ff006e",
  "#fb5607",
  "#ffbe0b",
  "#3a86ff",
];

function buildConfettiPieces(count = 220) {
  return Array.from({ length: count }, (_, i) => {
    const left = (i * 17 + (i % 7) * 3) % 100;
    const dx = ((i * 29) % 120) - 60;
    const rot = (i * 37) % 720;
    const delay = (i % 40) * 0.08;
    const duration = 2.8 + (i % 12) * 0.35;
    const size = 6 + (i % 5) * 3;
    const shape = i % 3;
    return {
      id: i,
      left,
      dx,
      rot,
      delay,
      duration,
      size,
      shape,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    };
  });
}

const CELEBRATION_CONFETTI = buildConfettiPieces(220);

function CelebrationConfetti() {
  return (
    <div className="tg-celebration-confetti" aria-hidden="true">
      {CELEBRATION_CONFETTI.map((p) => (
        <span
          key={p.id}
          className={`tg-confetti-piece tg-confetti-piece--${p.shape}`}
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: p.shape === 0 ? `${p.size * 0.45}px` : `${p.size}px`,
            backgroundColor: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--dx": `${p.dx}px`,
            "--rot": `${p.rot}deg`,
          }}
        />
      ))}
    </div>
  );
}

function TabooCardPanel({
  isHost,
  canSeeWord,
  selectedWord,
  tabooWords,
  releasedTabooIndices,
  autoRevealedTabooIndices,
  isGuessing,
  remainingSec,
  lobbyName,
  onFlip,
  flipping,
  canFlip,
}) {
  const pills = (tabooWords.length >= 3 ? tabooWords : ["", "", ""]).slice(0, 3);
  const releasedSet = new Set(releasedTabooIndices);
  const autoSet = new Set(autoRevealedTabooIndices);

  const handleReleaseTaboo = (index) => {
    if (!isHost || !lobbyName) return;
    void releaseTabooWord(lobbyName, index);
  };

  const displayWord = String(selectedWord || "…").toUpperCase();

  return (
    <section className="tg-panel tg-panel--host" aria-label="Word card">
      <p className="tg-label">{canSeeWord ? "Your Word" : "The Word"}</p>
      {canSeeWord ? (
        <h2 className="tg-word">{displayWord}</h2>
      ) : (
        <h2 className="tg-word tg-word--hidden">???</h2>
      )}

      <p className="tg-timer tg-timer--word">{isGuessing ? `${remainingSec}s` : "—"}</p>

      <p className="tg-sub">Taboo Words</p>
      <div className="tg-taboo-pills">
        {pills.map((t, i) => {
          // Host + selected player see everything.
          // "Auto revealed" taboo pills should be per-user (only for the guesser).
          const released = canSeeWord || releasedSet.has(i) || autoSet.has(i);
          const text = String(t || "").trim();
          if (isHost) {
            return (
              <button
                key={i}
                type="button"
                className={`tg-taboo-pill tg-taboo-pill--btn${released ? " tg-taboo-pill--released" : ""}`}
                onClick={() => handleReleaseTaboo(i)}
                disabled={!isGuessing || released || !text}
                title={released ? "Released to players" : "Tap to release to players"}
              >
                {text || `Taboo ${i + 1}`}
              </button>
            );
          }
          return (
            <div
              key={i}
              className={`tg-taboo-pill${released ? "" : " tg-taboo-pill--hidden"}`}
            >
              {released ? text || "—" : "???"}
            </div>
          );
        })}
      </div>

      {isHost && isGuessing && (
        <p className="tg-host-hint">Tap a taboo word to show it to players</p>
      )}

      {isHost && canFlip && (
        <div className="tg-flip-wrap">
          <button
            type="button"
            className="tg-flip-btn"
            onClick={() => void onFlip()}
            disabled={flipping}
            aria-label="Flip to a different question"
          >
            {flipping ? "Flipping…" : "Flip"}
          </button>
        </div>
      )}

      {!isHost && (
        <p className="tg-timer">{isGuessing ? `${remainingSec}s` : "—"}</p>
      )}
    </section>
  );
}

function GuessPanel({
  isHost,
  guess,
  setGuess,
  canGuess,
  currentUserId,
  sending,
  onSubmit,
  onGuessKeyDown,
  guessInputRef,
  canSeeWord,
  recentGuesses,
  isGuessing,
  remainingSec,
}) {
  const guessListRef = useRef(null);
  const latestGuessKey = recentGuesses[0]?.id ?? recentGuesses.length;

  useEffect(() => {
    const el = guessListRef.current;
    if (!el || recentGuesses.length === 0) return;
    requestAnimationFrame(() => {
      el.scrollTop = 0;
    });
  }, [latestGuessKey, recentGuesses.length]);

  return (
    <section className="tg-panel tg-panel--player" aria-label={isHost ? "Live guesses" : "Submit guess"}>
      <p className="tg-guess-title">{isHost ? "Live Guesses" : "Submit your guess"}</p>

      {!isHost && (
        <div className="tg-guess-input-wrap">
          <input
            ref={guessInputRef}
            className="tg-guess-input"
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            onKeyDown={onGuessKeyDown}
            placeholder={canGuess ? "Type your guess…" : "You can't guess this round"}
            disabled={!canGuess}
            maxLength={120}
            aria-label="Your guess"
          />
          <button
            type="button"
            className="tg-submit"
            onClick={() => void onSubmit()}
            disabled={!canGuess || !guess.trim() || sending}
          >
            Submit
          </button>
        </div>
      )}

      {isHost && (
        <p className="tg-host-watch">Players type their guesses on the right →</p>
      )}

      <p className="tg-recent-title">Recent Guesses</p>
      <div className="tg-guess-list-wrap">
      <ul ref={guessListRef} className="tg-guess-list">
        {recentGuesses.length === 0 ? (
          <li className="tg-guess-row">
            <span className="tg-guess-word" style={{ opacity: 0.6 }}>
              {isHost ? "Waiting for guesses…" : "No guesses yet"}
            </span>
          </li>
        ) : (
          recentGuesses.map((msg) => {
            const { label, cls } = chatRowDisplay(msg, currentUserId);
            const playerName = String(msg?.name || "Player");
            const isMine = currentUserId ? String(msg?.userId ?? "") === String(currentUserId) : false;
            const shouldMaskExactWord =
              !canSeeWord && !isMine && (msg.status === "correct" || msg.status === "taboo");
            const displayText = shouldMaskExactWord ? "—" : msg.text;
            return (
              <li key={msg.id} className="tg-guess-row">
                <span className="tg-guess-meta">
                  <span className="tg-guess-name">{playerName}</span>
                  <span className="tg-guess-word">{displayText}</span>
                </span>
                {label ? (
                  <span className={`tg-guess-status ${cls}`}>{label}</span>
                ) : null}
              </li>
            );
          })
        )}
      </ul>
      </div>

      {isGuessing && !isHost && (
        <p className="tg-timer tg-timer--inline">{remainingSec}s left</p>
      )}
    </section>
  );
}

export default function TabooGame() {
  const navigate = useNavigate();
  const location = useLocation();
  const [guess, setGuess] = useState("");
  const [sending, setSending] = useState(false);
  const [flipping, setFlipping] = useState(false);
  const reportSyncedLobbyRef = useRef("");
  const finalizedRef = useRef(false);
  const guessInputRef = useRef(null);
  const [selectedId, setSelectedId] = useState("");
  const [startingRound, setStartingRound] = useState(false);
  const [leaders, setLeaders] = useState([]);
  const [leadersLoading, setLeadersLoading] = useState(false);
  const [leadersError, setLeadersError] = useState(null);

  const {
    lobbyName,
    userId,
    name,
    isHost,
    phase,
    lobbyPhase,
    turnPhase,
    selectedWord,
    tabooWords,
    releasedTabooIndices,
    guessedPoints,
    correctGuessers,
    players,
    game,
    remainingSec,
    timerSeconds,
    turnIndex,
    chatMessages,
    currentDrawerId,
    joining,
    configured,
    flipIndex,
    shownWordKeys,
    overallTimerTotal,
    overallTimerRemaining,
    overallTimerPaused,
  } = useScribbleGame();

  const { questions, timeDurationMin, loading: wordsLoading } = useLobbyWords(lobbyName);

  const isSelectedPlayer = Boolean(userId && currentDrawerId && String(userId) === String(currentDrawerId));
  const alreadyCorrect = Boolean(userId && correctGuessers[String(userId)]);
  const canSeeWord = isHost || isSelectedPlayer || alreadyCorrect;

  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const sessionId = storedUserData.sessionId;
  const organizationId = storedUserData.organizationId;
  const token = sessionId && organizationId ? `${sessionId}&${organizationId}` : "";

  const correctGuesserIdsSet = useMemo(() => {
    const raw = game?.lastRoundCorrectGuessers || {};
    if (!raw || typeof raw !== "object") return new Set();
    return new Set(Object.keys(raw).map(String));
  }, [game?.lastRoundCorrectGuessers]);

  const scoreByUserId = useMemo(() => {
    const map = new Map();
    leaders.forEach((row, i) => {
      const id = String(row.userId ?? row.userid ?? row.id ?? "");
      if (id) map.set(id, { points: Number(row.total_score) || 0, rank: i + 1 });
    });
    return map;
  }, [leaders]);

  const roster = useMemo(() => {
    const list = (players || []).filter((p) => !p.isHost);
    const withScores = list.map((p) => {
      const id = String(p.id);
      return {
        ...p,
        points: scoreByUserId.get(id)?.points ?? 0,
      };
    });
    withScores.sort((a, b) => b.points - a.points || String(a.name).localeCompare(String(b.name)));
    return withScores.map((p, i) => ({
      ...p,
      rank: i + 1,
    }));
  }, [players, scoreByUserId]);

  useEffect(() => {
    document.documentElement.classList.add("taboo-game-static-bg");
    document.body.classList.add("taboo-game-static-bg");
    markScribbleGameStarted();
    return () => {
      document.documentElement.classList.remove("taboo-game-static-bg");
      document.body.classList.remove("taboo-game-static-bg");
    };
  }, []);

  useEffect(() => {
    const key = String(lobbyName ?? "").trim();
    if (!key || reportSyncedLobbyRef.current === key) return;
    reportSyncedLobbyRef.current = key;
    let score = 0;
    try {
      const raw = sessionStorage.getItem("scribbleLastTotalScore");
      if (raw != null && String(raw).trim() !== "") score = Number(raw) || 0;
    } catch {
      /* ignore */
    }
    syncScribbleScoreToExtraMile(score, { timeOverride: "00:00" }).catch((e) => {
      console.warn("[taboo] initial report sync:", e?.message || e);
    });
  }, [lobbyName]);

  useEffect(() => {
    if (phase !== "completed" || finalizedRef.current) return undefined;
    finalizedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        await finalizeScribblePlayer();
      } catch (e) {
        console.error(e);
      }
      if (!cancelled) {
        navigate("/leaderboard", {
          replace: true,
          state: { lobby_name: lobbyName, ...(location.state || {}) },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, navigate, lobbyName, location.state]);

  useEffect(() => {
    if (remainingSec === 0 && !overallTimerPaused && lobbyPhase === "playing" && !finalizedRef.current) {
      finalizedRef.current = true;
      (async () => {
        try {
          await finalizeScribblePlayer();
          const { scribbleLobbyRef } = await import("../../lib/scribbleLobby");
          const { update } = await import("../../lib/firebaseClient");
          const r = scribbleLobbyRef(lobbyName);
          if (r) {
            await update(r, {
              phase: "completed",
              "game/turnPhase": "ended",
              "game/timerEndsAt": null,
              updatedAt: Date.now(),
            });
          }
        } catch (e) {
          console.error("Error completing game on timer expiry:", e);
        }
        navigate("/leaderboard", {
          replace: true,
          state: { lobby_name: lobbyName, ...(location.state || {}) },
        });
      })();
    }
  }, [remainingSec, overallTimerPaused, lobbyPhase, lobbyName, navigate, location.state]);

  useEffect(() => {
    if (lobbyPhase === "select_player") {
      setSelectedId("");
      if (overallTimerRemaining === 0 && overallTimerTotal > 0) {
        (async () => {
          try {
            await finalizeScribblePlayer();
            const { scribbleLobbyRef } = await import("../../lib/scribbleLobby");
            const { update } = await import("../../lib/firebaseClient");
            const r = scribbleLobbyRef(lobbyName);
            if (r) {
              await update(r, {
                phase: "completed",
                "game/turnPhase": "ended",
                "game/timerEndsAt": null,
                updatedAt: Date.now(),
              });
            }
          } catch (e) {
            console.error("Error completing game on timer expiry:", e);
          }
          navigate("/leaderboard", {
            replace: true,
            state: { lobby_name: lobbyName, ...(location.state || {}) },
          });
        })();
      }
    }
  }, [lobbyPhase, overallTimerRemaining, overallTimerTotal, lobbyName, navigate, location.state]);

  useEffect(() => {
    if (lobbyPhase !== "select_player") return;
    if (!lobbyName || !token || !sessionId || !organizationId) return;

    let cancelled = false;
    setLeadersLoading(true);
    setLeadersError(null);

    (async () => {
      try {
        const data = await fetchLeaderboard({ token, sessionId, organizationId, lobby_name: lobbyName });
        if (cancelled) return;
        const key = lobbyName.trim();
        const sorted = Array.isArray(data)
          ? [...data]
              .filter((row) => String(row.lobby_name ?? "").trim() === key)
              .sort((a, b) => (Number(b.total_score) || 0) - (Number(a.total_score) || 0))
          : [];
        setLeaders(sorted);
      } catch (e) {
        if (!cancelled) setLeadersError(e?.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLeadersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lobbyPhase, lobbyName, token, sessionId, organizationId, turnIndex]);

  const recentGuesses = useMemo(() => [...chatMessages].reverse().slice(0, 12), [chatMessages]);

  const isGuessing = turnPhase === "guessing" || turnPhase === "drawing";
  const canGuess =
    !isHost &&
    isGuessing &&
    turnPhase !== "round_end" &&
    !alreadyCorrect &&
    !sending &&
    !isSelectedPlayer;
  const hasFlipCandidate = useMemo(
    () => Boolean(pickAlternateTabooCard(questions, shownWordKeys)),
    [questions, shownWordKeys]
  );

  const canFlip =
    isHost &&
    isGuessing &&
    turnPhase !== "round_end" &&
    !flipping &&
    !wordsLoading &&
    questions.length > 0 &&
    hasFlipCandidate;

  const handleFlip = useCallback(async () => {
    if (!canFlip || !lobbyName) return;
    const nextCard = pickAlternateTabooCard(questions, shownWordKeys);
    if (!nextCard) {
      await Swal.fire(
        "No new card",
        "Every question for this round has already been shown. End the round or wait for the timer.",
        "info"
      );
      return;
    }
    setFlipping(true);
    try {
      const result = await flipTabooCard(lobbyName, nextCard);
      if (!result?.ok) {
        if (result?.reason === "already_shown") {
          await Swal.fire("Already shown", "That card was used earlier this round. Try flipping again.", "info");
        } else if (result?.reason === "invalid_card") {
          await Swal.fire("Invalid card", "Could not load that question.", "warning");
        } else {
          await Swal.fire("Cannot flip", "The round is not active.", "warning");
        }
      }
    } catch (e) {
      await Swal.fire("Error", e?.message || "Could not flip card.", "error");
    } finally {
      setFlipping(false);
    }
  }, [canFlip, lobbyName, questions, shownWordKeys]);

  const autoRevealedTabooIndices = useMemo(() => {
    const tabNorms = (tabooWords || []).map((t) => normalizeGuess(t));
    const hit = new Set();
    if (!tabNorms.some(Boolean)) return [];
    const me = userId ? String(userId) : "";
    (chatMessages || []).forEach((m) => {
      if (!me) return;
      if (String(m?.userId ?? "") !== me) return;
      const g = normalizeGuess(m?.text);
      if (!g) return;
      tabNorms.forEach((w, i) => {
        if (w && g === w) hit.add(i);
      });
    });
    return Array.from(hit);
  }, [tabooWords, chatMessages, userId]);

  const submitGuess = useCallback(async () => {
    const text = guess.trim();
    if (!text || !lobbyName || !canGuess) return;
    setSending(true);
    try {
      const pointsEarned = pointsForCorrectGuess(remainingSec, timerSeconds);
      const drawerPoints = pointsForDrawerOnCorrect(pointsEarned);
      const result = await postChatGuess(lobbyName, {
        userId,
        name,
        text,
        answerWord: selectedWord,
        pointsEarned,
      });
      if (result?.ok && result.status === "correct") {
        try {
          const scored = await recordCorrectGuess({
            guessText: text,
            turnIndex,
            remainingSec,
            timerSeconds,
            points: pointsEarned,
          });
          if (currentDrawerId && drawerPoints > 0) {
            await recordDrawerBonus({
              drawerUserId: currentDrawerId,
              turnIndex,
              points: drawerPoints,
            });
          }
        } finally {
          if (result.roundComplete) {
            await enterRoundEnd(lobbyName);
          }
        }
      }
      setGuess("");
      requestAnimationFrame(() => {
        guessInputRef.current?.focus();
      });
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
      if (canGuess) {
        requestAnimationFrame(() => {
          guessInputRef.current?.focus();
        });
      }
    }
  }, [
    guess,
    lobbyName,
    canGuess,
    userId,
    name,
    selectedWord,
    remainingSec,
    timerSeconds,
    turnIndex,
    currentDrawerId,
  ]);

  const onGuessKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void submitGuess();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!configured || joining) {
    return (
      <main className="tg-page">
        <p className="tg-waiting">Connecting to game…</p>
      </main>
    );
  }

  if (lobbyPhase === "select_player") {
    const cardForTurn = wordOptionsForTurn(questions, turnIndex, 1)[0] || null;
    const selectedPlayer = roster.find((p) => p.id === selectedId);

    const handleStartRound = async () => {
      if (overallTimerRemaining === 0 && overallTimerTotal > 0) {
        await Swal.fire("Time's up!", "The game timer has expired.", "info");
        return;
      }
      if (!selectedId || !cardForTurn || !lobbyName) {
        await Swal.fire("Select player", "Choose a player and ensure cards are loaded.", "warning");
        return;
      }
      setStartingRound(true);
      try {
        const timerSec = Math.max(1, Number(timeDurationMin) || 1) * 60;
        await startTabooRound(lobbyName, {
          playerId: selectedId,
          word: cardForTurn.word,
          tabooWords:
            cardForTurn.taboo?.length >= 3
              ? cardForTurn.taboo
              : String(cardForTurn.hint || "")
                  .split(/[,·|]/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 3),
          timerSeconds: timerSec,
          turnIndex,
        });
      } catch (e) {
        await Swal.fire("Error", e?.message || "Could not start round.", "error");
      } finally {
        setStartingRound(false);
      }
    };

    return (
      <main className="snp-page" aria-label="Between rounds">
        <div className="tg-confetti" aria-hidden="true" />
        {overallTimerTotal > 0 && (
          <div style={{ 
            textAlign: 'center', 
            fontSize: '1.2rem', 
            fontWeight: 'bold', 
            color: overallTimerRemaining <= 30 ? '#ef476f' : '#ffd166',
            marginBottom: '1rem',
            padding: '0.5rem'
          }}>
            Time Remaining: {formatTime(overallTimerRemaining)} (Paused)
          </div>
        )}
        <div className="snp-inner">
          <section className="snp-leaderboard" aria-label="Player list">
            <div className="snp-table-head">
              <span>No.</span>
              <span>Name</span>
              <span>Points</span>
            </div>

            {leadersLoading ? (
              <p style={{ textAlign: "center", fontWeight: 700, opacity: 0.9 }}>Loading leaderboard…</p>
            ) : leadersError ? (
              <p style={{ textAlign: "center", fontWeight: 700, color: "#ffd166" }}>{leadersError}</p>
            ) : roster.length === 0 ? (
              <p style={{ textAlign: "center", fontWeight: 700 }}>Waiting for players…</p>
            ) : (
              <div className="snp-player-rows">
                {roster.map((p) => {
                  const isCorrect = correctGuesserIdsSet.has(String(p.id));
                  const rowClass = `snp-table-row${isCorrect ? " snp-table-row--correct" : ""}${
                    isHost ? " snp-table-row--pick" : ""
                  }${selectedId === p.id && isHost ? " snp-table-row--selected" : ""}`;

                  if (!isHost) {
                    return (
                      <div key={p.id} className={rowClass}>
                        <span>{p.rank}</span>
                        <span>{p.name}</span>
                        <span>{p.points}</span>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={rowClass}
                      onClick={() => setSelectedId(p.id)}
                      disabled={startingRound}
                    >
                      <span>{p.rank}</span>
                      <span>{p.name}</span>
                      <span>{p.points}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {isHost ? (
            <section className="snp-next-card" aria-label="Next player">
              <h2 className="snp-next-title">Next</h2>
              <svg
                className="snp-person-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
              <p className="snp-next-status">{selectedPlayer ? selectedPlayer.name : "No player selected"}</p>
              <button
                type="button"
                className="snp-select-btn"
                onClick={() => void handleStartRound()}
                disabled={startingRound || roster.length === 0 || !selectedId || wordsLoading || !cardForTurn}
              >
                {startingRound ? "Starting…" : selectedId ? "Start round" : "Select"}
              </button>
            </section>
          ) : (
            <section className="snp-next-card" aria-label="Waiting for host">
              <h2 className="snp-next-title">Waiting</h2>
              <p className="snp-next-status">Host is selecting the next player…</p>
            </section>
          )}
        </div>
      </main>
    );
  }

  const myCorrectEntry = userId ? correctGuessers[String(userId)] : null;
  const showCelebration = turnPhase === "round_end" && Boolean(myCorrectEntry);
  const celebrationPoints = Math.max(0, Number(myCorrectEntry?.points) || guessedPoints || 0);

  return (
    <main className="tg-page" aria-label="Equity Taboo game">
      <div className="tg-confetti" aria-hidden="true" />
      <div className="tg-inner">
        <p className="tg-round-label">
          Round {turnIndex + 1}
          {isHost ? " · You are the host" : isSelectedPlayer ? " · You are the word holder" : " · Guess the word"}
          {overallTimerTotal > 0 && (
            <span style={{ marginLeft: '1rem', fontWeight: 'bold', color: remainingSec <= 30 ? '#ef476f' : '#ffd166' }}>
              {' · Time: '}{formatTime(remainingSec)}
            </span>
          )}
        </p>

        {showCelebration && (
          <div className="tg-celebration" role="dialog" aria-live="polite">
            <CelebrationConfetti />
            <div className="tg-celebration-card">
              <h2>Congratulations!</h2>
              <p>Amazing — you guessed the word!</p>
              {celebrationPoints > 0 && (
                <p className="tg-celebration-points">+{celebrationPoints} points</p>
              )}
            </div>
          </div>
        )}

        <div className="tg-duo">
          <TabooCardPanel
            isHost={isHost}
            canSeeWord={canSeeWord}
            selectedWord={selectedWord}
            tabooWords={tabooWords}
            releasedTabooIndices={releasedTabooIndices}
            autoRevealedTabooIndices={autoRevealedTabooIndices}
            isGuessing={isGuessing}
            remainingSec={remainingSec}
            lobbyName={lobbyName}
            onFlip={handleFlip}
            flipping={flipping}
            canFlip={canFlip}
          />
          <GuessPanel
            isHost={isHost}
            guess={guess}
            setGuess={setGuess}
            canGuess={canGuess}
            currentUserId={userId}
            sending={sending}
            onSubmit={submitGuess}
            onGuessKeyDown={onGuessKeyDown}
            guessInputRef={guessInputRef}
            canSeeWord={canSeeWord}
            recentGuesses={recentGuesses}
            isGuessing={isGuessing}
            remainingSec={remainingSec}
          />
        </div>
      </div>
    </main>
  );
}
